<template>
  <div class="single-component-demo">
    <!-- 页面标题 -->
    <div class="header">
      <h1>Vue2 数字滚动 - 单组件内使用</h1>
      <p class="subtitle">无需全局注册，完全在单个组件内使用</p>
      <div class="status" :class="statusClass">{{ statusMessage }}</div>
    </div>
    
    <!-- 控制面板 -->
    <div class="control-panel">
      <div class="config-card">
        <h3>配置选项</h3>
        
        <div class="form-group">
          <label>目标数字：</label>
          <input v-model.number="config.number" type="number" min="1" class="input">
        </div>
        
        <div class="form-group">
          <label>持续时间(ms)：</label>
          <input v-model.number="config.duration" type="number" min="100" step="100" class="input">
        </div>
        
        <div class="form-group">
          <label>缓动函数：</label>
          <select v-model="config.easing" class="select">
            <option value="linear">linear</option>
            <option value="easeIn">easeIn</option>
            <option value="easeOut">easeOut</option>
            <option value="easeInOut">easeInOut</option>
            <option value="bounce">bounce</option>
          </select>
        </div>
        
        <div class="form-group">
          <label>分隔符：</label>
          <input v-model="config.separator" type="text" class="input" placeholder="如：,">
        </div>
        
        <div class="form-group">
          <label>前缀：</label>
          <input v-model="config.prefix" type="text" class="input" placeholder="如：$">
        </div>
        
        <div class="form-group">
          <label>后缀：</label>
          <input v-model="config.suffix" type="text" class="input" placeholder="如：元">
        </div>
        
        <div class="button-group">
          <button @click="updateDemo" class="btn btn-primary">应用配置</button>
          <button @click="resetAllNumbers" class="btn btn-secondary">重置所有</button>
          <button @click="testMethods" class="btn btn-success">测试方法</button>
        </div>
      </div>
      
      <div class="info-card">
        <h3>使用说明</h3>
        <ul>
          <li>✅ 无需全局注册插件</li>
          <li>✅ 完全在单个组件内使用</li>
          <li>✅ 支持响应式更新</li>
          <li>✅ 支持手动控制</li>
          <li>✅ 支持多种配置选项</li>
        </ul>
        
        <h4>可用方法：</h4>
        <pre class="code-block">// 手动触发动画
this.localNumberScroll.trigger(element, number, options)

// 重置单个元素
this.localNumberScroll.reset(element)

// 重置所有元素
this.localNumberScroll.resetAll()

// 直接调用动画函数
this.animateNumber(element, number, options)</pre>
      </div>
    </div>
    
    <!-- 滚动提示 -->
    <div class="scroll-hint">
      <div class="arrow">↓</div>
      <p>向下滚动触发数字动画</p>
    </div>
    
    <!-- 占位区域 -->
    <div class="spacer"></div>
    
    <!-- 自动动画区域 -->
    <div class="auto-animation-section">
      <h2>自动触发动画（滚动到视口时）</h2>
      <div class="number-grid">
        <div class="number-card">
          <div class="number-label">基础数字</div>
          <div 
            class="number-display"
            v-number-scroll="config.number">
            0
          </div>
          <div class="number-desc">滚动到此区域触发</div>
        </div>
        
        <div class="number-card">
          <div class="number-label">带千分位</div>
          <div 
            class="number-display"
            v-number-scroll="{
              number: config.number,
              separator: config.separator
            }">
            0
          </div>
          <div class="number-desc">使用分隔符：{{ config.separator || '无' }}</div>
        </div>
        
        <div class="number-card">
          <div class="number-label">带前后缀</div>
          <div 
            class="number-display"
            v-number-scroll="{
              number: config.number,
              prefix: config.prefix,
              suffix: config.suffix
            }">
            {{ config.prefix }}0{{ config.suffix }}
          </div>
          <div class="number-desc">前缀: {{ config.prefix || '无' }} 后缀: {{ config.suffix || '无' }}</div>
        </div>
        
        <div class="number-card">
          <div class="number-label">完整配置</div>
          <div 
            class="number-display"
            v-number-scroll="fullConfig">
            {{ config.prefix }}0{{ config.suffix }}
          </div>
          <div class="number-desc">延迟500ms，阈值0.2</div>
        </div>
      </div>
    </div>
    
    <!-- 更多占位 -->
    <div class="spacer"></div>
    
    <!-- 手动控制区域 -->
    <div class="manual-control-section">
      <h2>手动控制区域</h2>
      
      <div class="manual-demo">
        <div class="manual-number" ref="manualNumber">
          点击按钮开始动画
        </div>
        
        <div class="manual-controls">
          <div class="input-group">
            <input 
              v-model.number="manualTarget" 
              type="number" 
              min="1" 
              class="input"
              placeholder="输入目标数字">
            <button @click="startManualAnimation" class="btn btn-primary">
              开始动画
            </button>
          </div>
          
          <div class="button-group">
            <button @click="startBounceAnimation" class="btn btn-warning">
              弹跳效果
            </button>
            <button @click="startCustomAnimation" class="btn btn-info">
              自定义动画
            </button>
            <button @click="resetManualAnimation" class="btn btn-secondary">
              重置
            </button>
          </div>
        </div>
        
        <div class="method-examples">
          <h4>代码示例：</h4>
          <pre class="code-example">// 使用局部指令
&lt;div v-number-scroll="1000"&gt;0&lt;/div&gt;

// 使用对象配置
&lt;div v-number-scroll="{ number: 1000, duration: 3000 }"&gt;0&lt;/div&gt;

// 手动触发
this.localNumberScroll.trigger(element, 5000, {
  duration: 2000,
  easing: 'easeOut',
  separator: ','
})</pre>
        </div>
      </div>
      
      <!-- 多个测试元素 -->
      <div class="test-grid">
        <div class="test-item">
          <div class="test-number" ref="testNumber1">0</div>
          <button @click="triggerTest1" class="btn btn-sm">触发到 2500</button>
        </div>
        
        <div class="test-item">
          <div class="test-number" ref="testNumber2">0</div>
          <button @click="triggerTest2" class="btn btn-sm">触发到 8888</button>
        </div>
        
        <div class="test-item">
          <div class="test-number" ref="testNumber3">0</div>
          <button @click="resetTest3" class="btn btn-sm">重置此数字</button>
        </div>
      </div>
    </div>
    
    <!-- 静态示例 -->
    <div class="static-examples">
      <h2>静态配置示例</h2>
      <div class="examples-row">
        <div class="example">
          <h4>快速数字</h4>
          <div class="example-number" v-number-scroll="{ number: 500, duration: 1000 }">0</div>
          <code>v-number-scroll="{ number: 500, duration: 1000 }"</code>
        </div>
        
        <div class="example">
          <h4>缓慢数字</h4>
          <div class="example-number" v-number-scroll="{ number: 1000, duration: 5000 }">0</div>
          <code>v-number-scroll="{ number: 1000, duration: 5000 }"</code>
        </div>
        
        <div class="example">
          <h4>带格式</h4>
          <div class="example-number" v-number-scroll="{ number: 1234567, separator: ',', prefix: '¥' }">¥0</div>
          <code>v-number-scroll="{ number: 1234567, separator: ',', prefix: '¥' }"</code>
        </div>
        
        <div class="example">
          <h4>弹跳效果</h4>
          <div class="example-number" v-number-scroll="{ number: 999, duration: 3000, easing: 'bounce' }">0</div>
          <code>v-number-scroll="{ number: 999, duration: 3000, easing: 'bounce' }"</code>
        </div>
      </div>
    </div>
    
    <!-- 页脚 -->
    <footer class="footer">
      <p>Vue2 数字滚动插件 - 单组件内使用 | 无需全局注册</p>
    </footer>
  </div>
</template>

<script>
// 在单组件内导入数字滚动功能
import { 
  createNumberScrollDirective, 
  animateNumber,
  numberScrollMethods 
} from '@/utils/VueNumberScroll.js';

export default {
  name: 'SingleComponentDemo',
  
  // 在组件内注册局部指令
  directives: {
    'number-scroll': createNumberScrollDirective({
      duration: 2000,
      easing: 'easeOut',
      separator: ',',
      once: true
    })
  },
  
  data() {
    return {
      // 配置
      config: {
        number: 8888,
        duration: 2500,
        easing: 'easeOut',
        separator: ',',
        prefix: '$',
        suffix: ''
      },
      
      // 手动控制
      manualTarget: 7777,
      
      // 状态
      statusMessage: '插件已加载，指令和方法可用',
      statusClass: 'success'
    };
  },
  
  computed: {
    // 局部方法 - 方便在模板中使用
    localNumberScroll() {
      return numberScrollMethods;
    },
    
    // 完整配置
    fullConfig() {
      return {
        number: this.config.number,
        duration: this.config.duration,
        easing: this.config.easing,
        separator: this.config.separator,
        prefix: this.config.prefix,
        suffix: this.config.suffix,
        delay: 500,
        threshold: 0.2
      };
    }
  },
  
  methods: {
    // 更新演示
    updateDemo() {
      this.statusMessage = `配置已更新：目标数字 ${this.config.number}`;
      this.statusClass = 'info';
      
      // 重置所有动画以应用新配置
      this.resetAllNumbers();
      
      // 滚动到演示区域
      setTimeout(() => {
        const section = document.querySelector('.auto-animation-section');
        if (section) {
          section.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    },
    
    // 重置所有数字
    resetAllNumbers() {
      numberScrollMethods.resetAll();
      this.statusMessage = '所有数字动画已重置';
      this.statusClass = 'warning';
    },
    
    // 测试方法
    testMethods() {
      if (typeof animateNumber === 'function' && numberScrollMethods) {
        this.statusMessage = '✅ 所有方法都可用！';
        this.statusClass = 'success';
        console.log('可用方法：', Object.keys(numberScrollMethods));
      } else {
        this.statusMessage = '❌ 方法加载失败';
        this.statusClass = 'error';
      }
    },
    
    // 开始手动动画
    startManualAnimation() {
      const target = this.manualTarget || 1000;
      const el = this.$refs.manualNumber;
      
      if (!el) {
        this.statusMessage = '❌ 找不到目标元素';
        this.statusClass = 'error';
        return;
      }
      
      el.textContent = '0';
      el.style.color = '#4361ee';
      el.style.fontWeight = 'bold';
      
      // 使用局部方法
      numberScrollMethods.trigger(el, target, {
        duration: 3000,
        easing: 'easeInOut',
        separator: ',',
        prefix: '',
        suffix: ''
      });
      
      this.statusMessage = `开始手动动画：0 → ${target}`;
      this.statusClass = 'info';
      
      // 监听完成事件
      el.addEventListener('number-scroll-complete', () => {
        el.style.color = '#3a0ca3';
        this.statusMessage = `✅ 动画完成：${target}`;
        this.statusClass = 'success';
      }, { once: true });
    },
    
    // 弹跳效果动画
    startBounceAnimation() {
      const target = this.manualTarget || 1000;
      const el = this.$refs.manualNumber;
      
      if (!el) return;
      
      el.textContent = '0';
      el.style.color = '#e63946';
      el.style.fontWeight = 'bold';
      
      // 直接使用动画函数
      animateNumber(el, target, {
        duration: 4000,
        easing: 'bounce',
        separator: ',',
        prefix: '🎯 ',
        suffix: ' 分'
      });
      
      this.statusMessage = `开始弹跳动画：0 → ${target}`;
      this.statusClass = 'info';
    },
    
    // 自定义动画
    startCustomAnimation() {
      const target = 8888;
      const el = this.$refs.manualNumber;
      
      if (!el) return;
      
      el.textContent = '0';
      el.style.color = '#2a9d8f';
      el.style.fontWeight = 'bold';
      
      animateNumber(el, target, {
        duration: 5000,
        easing: 'easeInOut',
        separator: ',',
        prefix: '💰 $',
        suffix: ' 元',
        delay: 1000
      });
      
      this.statusMessage = `开始自定义动画：0 → ${target}`;
      this.statusClass = 'info';
    },
    
    // 重置手动动画
    resetManualAnimation() {
      const el = this.$refs.manualNumber;
      if (el) {
        el.textContent = '点击按钮开始动画';
        el.style.color = '#666';
        el.style.fontWeight = 'normal';
        this.statusMessage = '手动动画已重置';
        this.statusClass = 'warning';
      }
    },
    
    // 测试方法1
    triggerTest1() {
      const el = this.$refs.testNumber1;
      if (el) {
        numberScrollMethods.trigger(el, 2500, {
          duration: 2000,
          easing: 'easeOut',
          separator: ','
        });
      }
    },
    
    // 测试方法2
    triggerTest2() {
      const el = this.$refs.testNumber2;
      if (el) {
        animateNumber(el, 8888, {
          duration: 3000,
          easing: 'easeInOut',
          separator: ',',
          prefix: '🏆 ',
          suffix: ' 分'
        });
      }
    },
    
    // 重置测试3
    resetTest3() {
      const el = this.$refs.testNumber3;
      if (el) {
        numberScrollMethods.reset(el);
        this.statusMessage = '测试数字已重置';
        this.statusClass = 'warning';
      }
    }
  },
  
  mounted() {
    console.log('数字滚动指令已加载到当前组件');
    console.log('可用指令：', Object.keys(this.$options.directives));
    console.log('动画函数：', typeof animateNumber);
    console.log('局部方法：', numberScrollMethods);
    
    // 验证功能
    if (typeof animateNumber === 'function') {
      this.statusMessage = '✅ 数字滚动功能已成功加载';
      this.statusClass = 'success';
    }
  }
};
</script>

<style scoped>
.single-component-demo {
  max-width: 1400px;
  margin: 0 auto;
  padding: 20px;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  color: #333;
}

.header {
  text-align: center;
  margin-bottom: 40px;
  padding: 30px;
  background: linear-gradient(135deg, #4361ee 0%, #3a0ca3 100%);
  border-radius: 15px;
  color: white;
}

.header h1 {
  font-size: 2.5rem;
  margin-bottom: 15px;
}

.subtitle {
  font-size: 1.2rem;
  opacity: 0.9;
  margin-bottom: 20px;
}

.status {
  display: inline-block;
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 1.1rem;
  margin-top: 15px;
}

.status.success {
  background: rgba(46, 204, 113, 0.2);
  color: #27ae60;
  border: 2px solid #27ae60;
}

.status.info {
  background: rgba(52, 152, 219, 0.2);
  color: #2980b9;
  border: 2px solid #2980b9;
}

.status.warning {
  background: rgba(241, 196, 15, 0.2);
  color: #f39c12;
  border: 2px solid #f39c12;
}

.status.error {
  background: rgba(231, 76, 60, 0.2);
  color: #c0392b;
  border: 2px solid #c0392b;
}

.control-panel {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
  margin-bottom: 40px;
}

@media (max-width: 768px) {
  .control-panel {
    grid-template-columns: 1fr;
  }
}

.config-card, .info-card {
  background: white;
  padding: 30px;
  border-radius: 15px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08);
}

.config-card h3, .info-card h3 {
  color: #3a0ca3;
  margin-bottom: 25px;
  font-size: 1.5rem;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #555;
}

.input, .select {
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.3s ease;
}

.input:focus, .select:focus {
  outline: none;
  border-color: #4361ee;
  box-shadow: 0 0 0 3px rgba(67, 97, 238, 0.1);
}

.button-group {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 30px;
}

.btn {
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-primary {
  background: linear-gradient(135deg, #4361ee, #3a0ca3);
  color: white;
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(67, 97, 238, 0.3);
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn-secondary:hover {
  background: #5a6268;
  transform: translateY(-2px);
}

.btn-success {
  background: #28a745;
  color: white;
}

.btn-success:hover {
  background: #218838;
  transform: translateY(-2px);
}

.btn-warning {
  background: #ffc107;
  color: #212529;
}

.btn-warning:hover {
  background: #e0a800;
  transform: translateY(-2px);
}

.btn-info {
  background: #17a2b8;
  color: white;
}

.btn-info:hover {
  background: #138496;
  transform: translateY(-2px);
}

.btn-sm {
  padding: 8px 16px;
  font-size: 0.9rem;
}

.info-card ul {
  list-style: none;
  padding: 0;
  margin-bottom: 25px;
}

.info-card li {
  padding: 10px 0;
  border-bottom: 1px solid #eee;
  display: flex;
  align-items: center;
}

.info-card li:before {
  content: '✓';
  margin-right: 10px;
  color: #28a745;
  font-weight: bold;
}

.code-block {
  background: #2d3436;
  color: #dfe6e9;
  padding: 20px;
  border-radius: 8px;
  font-family: 'Courier New', monospace;
  font-size: 0.9rem;
  line-height: 1.5;
  overflow-x: auto;
}

.scroll-hint {
  text-align: center;
  margin: 40px 0;
  color: #4361ee;
  font-weight: 500;
}

.arrow {
  font-size: 2.5rem;
  animation: bounce 2s infinite;
  margin-bottom: 10px;
}

@keyframes bounce {
  0%, 20%, 50%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-15px);
  }
  60% {
    transform: translateY(-7px);
  }
}

.spacer {
  height: 800px;
  background: linear-gradient(transparent 49%, #e0e0e0 50%, transparent 51%);
  background-size: 100% 20px;
  margin: 40px 0;
  opacity: 0.3;
}

.auto-animation-section {
  background: white;
  padding: 40px;
  border-radius: 15px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08);
  margin-bottom: 40px;
}

.auto-animation-section h2 {
  text-align: center;
  color: #3a0ca3;
  margin-bottom: 40px;
  font-size: 2rem;
}

.number-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 30px;
}

.number-card {
  text-align: center;
  padding: 30px;
  background: #f8f9fa;
  border-radius: 12px;
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.number-card:hover {
  transform: translateY(-5px);
  border-color: #4361ee;
  box-shadow: 0 10px 25px rgba(67, 97, 238, 0.1);
}

.number-label {
  font-size: 1.1rem;
  color: #666;
  margin-bottom: 15px;
  font-weight: 600;
}

.number-display {
  font-size: 3.5rem;
  font-weight: 700;
  color: #4361ee;
  min-height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 15px;
  transition: color 0.3s ease;
}

.number-desc {
  font-size: 0.9rem;
  color: #888;
  line-height: 1.4;
}

.manual-control-section {
  background: white;
  padding: 40px;
  border-radius: 15px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08);
  margin-bottom: 40px;
}

.manual-control-section h2 {
  text-align: center;
  color: #3a0ca3;
  margin-bottom: 40px;
  font-size: 2rem;
}

.manual-demo {
  text-align: center;
  margin-bottom: 40px;
}

.manual-number {
  font-size: 4rem;
  font-weight: 700;
  color: #666;
  min-height: 140px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 30px;
  background: #f8f9fa;
  border-radius: 12px;
  transition: all 0.3s ease;
}

.manual-controls {
  margin-bottom: 30px;
}

.input-group {
  display: flex;
  gap: 10px;
  justify-content: center;
  margin-bottom: 20px;
}

.input-group .input {
  width: 200px;
}

.method-examples {
  background: #f8f9fa;
  padding: 25px;
  border-radius: 12px;
  text-align: left;
}

.method-examples h4 {
  color: #3a0ca3;
  margin-bottom: 15px;
}

.code-example {
  background: #2d3436;
  color: #dfe6e9;
  padding: 20px;
  border-radius: 8px;
  font-family: 'Courier New', monospace;
  font-size: 0.9rem;
  line-height: 1.5;
  overflow-x: auto;
}

.test-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 25px;
  margin-top: 30px;
}

.test-item {
  text-align: center;
  padding: 25px;
  background: #f8f9fa;
  border-radius: 12px;
  transition: transform 0.3s ease;
}

.test-item:hover {
  transform: translateY(-3px);
}

.test-number {
  font-size: 2.5rem;
  font-weight: 700;
  color: #4361ee;
  min-height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 15px;
  background: white;
  border-radius: 8px;
}

.static-examples {
  background: white;
  padding: 40px;
  border-radius: 15px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08);
  margin-bottom: 40px;
}

.static-examples h2 {
  text-align: center;
  color: #3a0ca3;
  margin-bottom: 40px;
  font-size: 2rem;
}

.examples-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 25px;
}

.example {
  text-align: center;
  padding: 25px;
  background: #f8f9fa;
  border-radius: 12px;
}

.example h4 {
  color: #3a0ca3;
  margin-bottom: 15px;
  font-size: 1.1rem;
}

.example-number {
  font-size: 2.2rem;
  font-weight: 700;
  color: #4361ee;
  min-height: 70px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 15px;
}

.example code {
  background: #eef1ff;
  padding: 10px;
  border-radius: 6px;
  font-size: 0.8rem;
  display: block;
  text-align: center;
}

.footer {
  text-align: center;
  padding: 30px;
  color: #666;
  border-top: 1px solid #e0e0e0;
  margin-top: 40px;
  font-size: 0.9rem;
}
</style>